import React from "react";
import { Link } from "@/components/LocaleLink";
import { ArrowUpRight, ArrowRight, Info } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { trackEvent, EVENTS } from "@/lib/analytics";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { getIndsatser } from "@/data/indsatser";

const IMG = "https://images.unsplash.com/photo-1714976694525-71eb29a7c500?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Ydelser = () => {
  const { t, lang } = useLang();
  const y = t.ydelser;
  const ind = getIndsatser(lang);

  return (
    <>
      <PageHero eyebrow={y.whatWeOffer} title={y.hero} intro={y.intro} image={IMG} />

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        {/* Bestiller note */}
        <Reveal>
          <div className="rounded-2xl bg-[#EAE5D9] border border-black/5 p-6 sm:p-7 flex gap-4" data-testid="ydelser-bestiller-note">
            <Info className="h-5 w-5 text-[#2D5A3F] shrink-0 mt-0.5" />
            <p className="text-[#1A2E24] leading-relaxed">{y.bestillerNote}</p>
          </div>
        </Reveal>

        {/* Categories */}
        <div className="mt-16 space-y-20">
          {y.categories.map((cat, ci) => (
            <div key={cat.key} data-testid={`ydelse-kategori-${cat.key}`}>
              <Reveal>
                <Eyebrow>{String.fromCharCode(65 + ci)}</Eyebrow>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1A2E24]">{cat.label}</h2>
              </Reveal>

              {cat.isProducts ? (
                <>
                  <Reveal delay={0.05}>
                    <p className="mt-4 text-[#5C7567] leading-relaxed max-w-2xl">{y.productsIntro}</p>
                  </Reveal>
                  <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {ind.products.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/indsatser/${p.slug}`}
                        className="group rounded-2xl bg-white border border-black/5 p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                        data-testid={`ydelse-forloeb-${p.slug}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-display text-2xl text-[#1A2E24]">{p.name}</h3>
                          <ArrowUpRight className="h-5 w-5 text-[#8EA694] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                        {p.tagline && <p className="text-[#3B5245] leading-relaxed">{p.tagline}</p>}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {cat.ref && (
                    <Reveal delay={0.05}>
                      <p className="mt-3 text-sm text-[#5C7567] italic max-w-2xl">{cat.ref}</p>
                    </Reveal>
                  )}
                  <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    {cat.items.map((item) => (
                      <div
                        key={item}
                        className="flex items-start gap-3 rounded-2xl bg-white border border-black/5 p-5"
                        data-testid={`ydelse-item-${cat.key}`}
                      >
                        <span className="mt-1 flex items-center justify-center h-6 w-6 rounded-full bg-[#EAE5D9] text-[#2D5A3F] shrink-0">
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </span>
                        <span className="text-[#1A2E24] leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {cat.cta && (
                <Reveal delay={0.1}>
                  <Link
                    to={`/kontakt?kilde=ydelser&indsatsomraade=${cat.area}&henvendelse=${cat.henv}`}
                    onClick={() => trackEvent(EVENTS.SERVICE_AREA_CONTACT_CLICK, { service_area: cat.area, source: "ydelser", language: lang })}
                    className="mt-8 group inline-flex items-center gap-2 text-[#2D5A3F] font-medium hover:text-[#1A2E24] transition-colors duration-300"
                    data-testid={`ydelse-cta-${cat.key}`}
                  >
                    {cat.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Reveal>
              )}
            </div>
          ))}
        </div>

        {/* General note */}
        <Reveal>
          <div className="mt-20 rounded-2xl border border-[#8EA694]/40 bg-white p-6 sm:p-7" data-testid="ydelser-general-note">
            <p className="text-[#3B5245] leading-relaxed">{y.generalNote}</p>
          </div>
        </Reveal>
      </section>

      <FinalCTA />
    </>
  );
};

export default Ydelser;
